import { FormApi } from 'final-form';
import React, { FunctionComponent, ReactNode, useEffect } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCreateEditEventFlowStore } from '~admin/store/create-edit-event-flow-store-provider';
import { getOfferGroupInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { offerGroupValidators } from '~admin/view/components/create-event/helper/create-event-validators.helper';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import {
  OfferGroupFields,
  OfferGroupFormData,
} from '~admin/view/components/details/offer-group-details/offer-group-fields.component';
import { isOfferGroupRequired } from '~admin/view/components/details/offer-group-details/offer-group-fields.helper';
import { WizardPageProps } from '~admin/view/components/ui/form/wizard.component';
import { MarketplaceType } from '~client/model/marketplace-type';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Column } from '~common/components/column.component';
import { Span } from '~common/components/span.component';
import { datetime, DateTimeFormatType, localizeDateTime } from '~common/helper/datetime';
import { Color } from '~common/model/basics';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface OfferGroupsProps extends FieldArrayRenderProps<OfferGroupFormData, HTMLDivElement> {
  marketplaceType: MarketplaceType;
  form?: FormApi<EventFormData>;
}

const OfferGroups: FunctionComponent<OfferGroupsProps> = ({ fields, marketplaceType, form }) => {
  const { t } = useTranslation();

  const offerGroupRequired = isOfferGroupRequired(marketplaceType);

  const onRemove = (index: number): void => {
    fields.remove(index);
  };

  const onAdd = (): void => {
    fields.push(getOfferGroupInitialValues());
  };

  useEffect((): void => {
    // if at least one offer group is required -> add it
    // and if it's not required -> remove all
    if (offerGroupRequired && fields.length === 0) {
      onAdd();
    } else if (!offerGroupRequired && fields.length >= 1) {
      fields.forEach(() => fields.pop());
    }
  }, []);

  return (
    <>
      {fields.map((name, offerGroupIndex) => (
        <OfferGroupFields
          key={name}
          name={name}
          marketplaceType={marketplaceType}
          offerGroupIndex={offerGroupIndex}
          onRemove={onRemove}
          form={form}
        />
      ))}
      {!offerGroupRequired && (
        <NoOfferGroupMessage>
          {t('createEvent.offerGroupDetails.noOfferGroupRequired')}
        </NoOfferGroupMessage>
      )}
      <ButtonBox>
        <Button color={Color.White} onClick={onAdd} disabled={!offerGroupRequired}>
          <StyledPlusIcon color={Color.Black} width={16} height={16} />
          {t('createEvent.offerGroupDetails.addOfferGroup')}
        </Button>
      </ButtonBox>
    </>
  );
};

interface MarketplaceProps {
  marketplaceType: MarketplaceType;
  marketplaceIndex: number;
  startDateTime: string;
  form?: FormApi<EventFormData>;
}

const Marketplace: FunctionComponent<MarketplaceProps> = ({
  marketplaceType,
  marketplaceIndex,
  startDateTime,
  form,
}) => {
  const { t } = useTranslation();

  return (
    <MarketplaceContainer key={marketplaceIndex}>
      <Subtitle>
        {t('createEvent.offerGroupDetails.subtitle', {
          num: marketplaceIndex + 1,
          type: t(
            `createEvent.marketplaceDetails.marketplaceType.enum.${
              marketplaceType ?? MarketplaceType.PURCHASE
            }`,
          ),
          datetime: localizeDateTime(datetime.parse(startDateTime), DateTimeFormatType.SHORT),
        })}
      </Subtitle>
      <FieldArray
        name={`marketplaces[${marketplaceIndex}].offerGroups`}
        validate={offerGroupValidators}
      >
        {({ fields, meta }): ReactNode => (
          <OfferGroups fields={fields} meta={meta} marketplaceType={marketplaceType} form={form} />
        )}
      </FieldArray>
    </MarketplaceContainer>
  );
};

export const OfferGroupDetails: FunctionComponent<WizardPageProps<EventFormData>> = ({
  formRenderProps,
}) => {
  const { t } = useTranslation();
  const createEventFlowStore = useCreateEditEventFlowStore();

  return (
    <Container>
      <Title>{t('createEvent.offerGroupDetails.title')}</Title>
      {createEventFlowStore.marketplaces.map(({ type, startDateTime }, marketplaceIndex) => (
        <Marketplace
          marketplaceType={type}
          marketplaceIndex={marketplaceIndex}
          startDateTime={startDateTime}
          form={formRenderProps.form}
          key={marketplaceIndex}
        />
      ))}
    </Container>
  );
};

const Container = styled(Column)`
  width: 100%;
`;

const MarketplaceContainer = styled(Column)`
  width: 100%;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding-top: 16px;
  padding-bottom: 32px;
  margin-bottom: 18px;
`;

const Title = styled(Span)`
  font-weight: bold;
  font-size: 22px;
  line-height: 22px;
  margin-bottom: 16px;
`;

const Subtitle = styled(Span)`
  font-weight: bold;
  font-size: 18px;
  line-height: 20px;
  margin: 4px auto 16px;
`;

const NoOfferGroupMessage = styled(Subtitle)`
  border-width: 2px;
  border-color: ${(props): string => props.theme.colors.gray.v3};
  border-style: solid hidden;
  margin: 32px 0;
  padding: 32px;
  text-align: center;
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 12px;
`;

const ButtonBox = styled(Box)`
   {
    padding-left: 28px;
  }
`;



// WEBPACK FOOTER //
// ./view/components/details/offer-group-details/offer-group-details.component.tsx